import { Link } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { BlinkingCursor, GlitchText, MatrixRain } from './ascii-art'

export function LHeroSection() {
  const { currentUser } = useAuth()

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0a0a] pt-20">
      {/* Matrix rain background */}
      <MatrixRain className="opacity-30" />

      {/* Gradient orbs */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#9B87F5]/10 rounded-full blur-[120px]"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#6E59A5]/10 rounded-full blur-[100px]"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{ duration: 6, repeat: Infinity }}
      />

      {/* L Character Image */}
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 0.15, x: 0 }}
        transition={{ duration: 1, delay: 0.5 }}
        className="absolute right-0 bottom-0 w-[400px] md:w-[500px] pointer-events-none"
      >
        <img
          src="/images/l-character.png"
          alt="L"
          className="w-full h-auto grayscale opacity-50"
        />
      </motion.div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-24">
        <div className="text-center space-y-8">
          {/* Giant L ASCII */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="flex justify-center"
          >
            <pre className="font-mono text-[#9B87F5] text-[10px] sm:text-[14px] md:text-[18px] leading-[1.1] select-none">
              {`    ██╗         
    ██║         
    ██║         
    ██║         
    ███████╗    
    ╚══════╝    `}
            </pre>
          </motion.div>

          {/* Tagline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <pre className="font-mono text-[#8E9196] text-[8px] sm:text-[10px] md:text-xs hidden sm:block">
              {`╔═══════════════════════════════════════════════════════════════════╗
║  "I will take a potato chip... and SHORTEN IT!"                   ║
╚═══════════════════════════════════════════════════════════════════╝`}
            </pre>
          </motion.div>

          {/* Main headline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="space-y-4"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white font-mono">
              <GlitchText>LINK</GlitchText>{' '}
              <span className="text-[#9B87F5]">SHORTENER</span>
            </h1>
            <p className="text-lg md:text-xl text-[#8E9196] font-mono max-w-2xl mx-auto">
              // The world's greatest detective of URLs
              <BlinkingCursor className="text-[#9B87F5] ml-1" />
            </p>
          </motion.div>

          {/* Interactive Link Shortener Preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-[#1A1F2C]/60 backdrop-blur-xl border border-[#9B87F5]/20 rounded-2xl p-6 max-w-xl mx-auto"
          >
            {/* Card Header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#9B87F5] to-[#6E59A5] flex items-center justify-center">
                  <span className="text-white font-bold text-sm">L</span>
                </div>
                <span className="text-white font-semibold">
                  Shorten your link
                </span>
              </div>
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#9B87F5]/30" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#9B87F5]/50" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#9B87F5]" />
              </div>
            </div>

            {/* URL Input Preview */}
            <div className="space-y-4">
              <div className="relative">
                <div className="bg-[#0a0a0a]/80 border border-[#9B87F5]/10 rounded-xl p-4 text-left">
                  <div className="text-[#8E9196] text-sm mb-1">
                    Your long URL
                  </div>
                  <div className="text-white/60 text-sm truncate font-mono">
                    https://example.com/very/long/url/that/needs/shortening...
                  </div>
                </div>
              </div>

              {/* Arrow Animation */}
              <motion.div
                className="flex justify-center"
                animate={{ y: [0, 4, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <div className="w-8 h-8 rounded-full bg-[#9B87F5]/20 flex items-center justify-center">
                  <ArrowRight className="w-4 h-4 text-[#9B87F5] rotate-90" />
                </div>
              </motion.div>

              {/* Shortened Link Result */}
              <div className="relative group">
                <motion.div
                  className="bg-gradient-to-r from-[#9B87F5]/20 to-[#6E59A5]/20 border border-[#9B87F5]/30 rounded-xl p-4"
                  animate={{
                    boxShadow: [
                      '0 0 0px rgba(155,135,245,0)',
                      '0 0 20px rgba(155,135,245,0.3)',
                      '0 0 0px rgba(155,135,245,0)',
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <div className="text-[#9B87F5] text-sm mb-1 font-medium">
                        Your short link
                      </div>
                      <div className="text-white text-lg font-mono font-bold">
                        l.zugl.in/surprise
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-9 h-9 rounded-lg bg-[#9B87F5]/10 flex items-center justify-center hover:bg-[#9B87F5]/20 transition-colors cursor-pointer">
                        <svg
                          className="w-4 h-4 text-[#9B87F5]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div className="w-9 h-9 rounded-lg bg-[#9B87F5]/10 flex items-center justify-center hover:bg-[#9B87F5]/20 transition-colors cursor-pointer">
                        <svg
                          className="w-4 h-4 text-[#9B87F5]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-2 mt-5">
              <span className="px-3 py-1 rounded-full bg-[#9B87F5]/10 text-[#9B87F5] text-xs font-medium">
                ✓ Custom aliases
              </span>
              <span className="px-3 py-1 rounded-full bg-[#9B87F5]/10 text-[#9B87F5] text-xs font-medium">
                ✓ Click analytics
              </span>
              <span className="px-3 py-1 rounded-full bg-[#9B87F5]/10 text-[#9B87F5] text-xs font-medium">
                ✓ QR codes
              </span>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
          >
            {currentUser ? (
              <Link to="/dashboard">
                <Button
                  size="lg"
                  className="bg-[#9B87F5] hover:bg-[#8B5CF6] text-black px-8 py-6 text-lg font-mono font-bold shadow-lg shadow-[#9B87F5]/25 transition-all duration-300 hover:shadow-xl hover:shadow-[#9B87F5]/30"
                >
                  [ OPEN DASHBOARD ]
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/sign-up">
                  <Button
                    size="lg"
                    className="bg-[#9B87F5] hover:bg-[#8B5CF6] text-black px-8 py-6 text-lg font-mono font-bold shadow-lg shadow-[#9B87F5]/25 transition-all duration-300 hover:shadow-xl hover:shadow-[#9B87F5]/30"
                  >
                    [ START INVESTIGATION ]
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/sign-in">
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-[#9B87F5]/30 text-[#9B87F5] hover:bg-[#9B87F5]/10 px-8 py-6 text-lg font-mono"
                  >
                    SIGN_IN
                  </Button>
                </Link>
              </>
            )}
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="flex items-center justify-center gap-8 md:gap-16 pt-12"
          >
            <div className="text-center">
              <pre className="font-mono text-[#9B87F5] text-xs mb-1">
                {`┌─────┐
│ ∞∞∞ │
└─────┘`}
              </pre>
              <div className="text-sm text-[#8E9196] font-mono">LINKS</div>
            </div>
            <div className="w-px h-16 bg-[#9B87F5]/20" />
            <div className="text-center">
              <pre className="font-mono text-[#9B87F5] text-xs mb-1">
                {`┌─────┐
│ 100 │
└─────┘`}
              </pre>
              <div className="text-sm text-[#8E9196] font-mono">UPTIME%</div>
            </div>
            <div className="w-px h-16 bg-[#9B87F5]/20" />
            <div className="text-center">
              <pre className="font-mono text-[#9B87F5] text-xs mb-1">
                {`┌─────┐
│ $0  │
└─────┘`}
              </pre>
              <div className="text-sm text-[#8E9196] font-mono">COST</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="font-mono text-[#9B87F5]/50 text-xs"
        >
          <pre>
            {`  ▼
 ▼▼▼
▼▼▼▼▼`}
          </pre>
        </motion.div>
      </motion.div>
    </section>
  )
}
